import React, { useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Row, Col, Spin } from "antd";
import styled from "styled-components";

import { SettingsIcon } from "../../assets/svg/SettingsIcon";
import FacilityIcon from "../../assets/svg/FacilityIcon";
import FacilityNameIcon from "../../assets/svg/FacilityNameIcon";
import {
  selectProvidersAllFacilities,
  selectFacilityById,
  selectUsers,
  selectCanEditFacilities
} from "../../store/selector";
import ACTIONS from "../../store/action";
import {
  Section,
  SectionTitle,
  BreadcrumbSection,
  BreadcrumbLeft,
  BreadcrumbItem,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
  MetaText,
  CreatedText,
  UpdatedText,
  StyledPrimaryButton
} from "../../component/sharedComponents/settingsEntity/components";
import {
  CompactDetailPageContainer,
  CompactDetailHeaderSection,
  Field,
  FieldLabel,
  FieldValue,
  PageHeading
} from "../../component/sharedComponents/settingsEntity/detail";
import { getUserName, dash } from "../../component/sharedComponents/settingsEntity/utils";
import { dateTimeFormatter } from "../../utils/date";
import { formatPhoneNumber } from "../../utils/formaters";
import { useStandardizedTracking } from "../../utils/pendo/useStandardizedTracking";
import { FACILITY_EVENTS, FACILITY_FEATURE_GROUP, FACILITY_PAGE_KEY } from "./analytics";

const FacilityUpdatedText = styled(UpdatedText)`
  margin-left: 0;
`;

// Meta line rules: once a facility has a last-update we show ONLY "Last updated…" (no Created line),
// and when the acting user can't be resolved we show only the date instead of a "User" placeholder.
const metaLineText = (t, keyWithUser, keyDateOnly, users, userId, date) => {
  const user = getUserName(users, userId, null);
  return user ? t(keyWithUser, { user, date }) : t(keyDateOnly, { date });
};

export const FacilityMetaLine = ({ facility, users, t }) => {
  const hasUpdate = Boolean(facility.updatedBy && facility.userUpdatedAt);
  return (
    <MetaText>
      {hasUpdate ? (
        <FacilityUpdatedText>
          {metaLineText(
            t,
            "facility_management.detail.updated_by",
            "facility_management.detail.updated_on",
            users,
            facility.updatedBy,
            dateTimeFormatter(facility.userUpdatedAt)
          )}
        </FacilityUpdatedText>
      ) : (
        <CreatedText>
          {metaLineText(
            t,
            "facility_management.detail.created_by",
            "facility_management.detail.created_on",
            users,
            facility.createdBy,
            dateTimeFormatter(facility.createdAt) || "-"
          )}
        </CreatedText>
      )}
    </MetaText>
  );
};

// Figma header actions (4036:28813): Edit is a Primary/Small 100×32 button — same dimensions as
// the edit page's Save button.
export const FacilityHeaderButton = styled(StyledPrimaryButton)`
  width: 100px;
  height: 32px;
  padding: 4px 8px;
  justify-content: center;
`;

const FacilityBreadcrumbLeft = styled(BreadcrumbLeft)`
  gap: 8px;

  > div {
    gap: 4px;
  }
`;

export const FacilityBreadcrumb = ({ current, history, t }) => (
  <FacilityBreadcrumbLeft>
    <BreadcrumbItem id="Facility_Breadcrumbs_Settings" onClick={() => history.push("/settings")}>
      <SettingsIcon />
      {t("facility_management.breadcrumb.settings")}
    </BreadcrumbItem>
    <BreadcrumbSeparator />
    <BreadcrumbItem id="Facility_Breadcrumbs_Facilities" onClick={() => history.push("/facilities-settings")}>
      <FacilityIcon style={{ width: 24, height: 24 }} />
      {t("facility_management.breadcrumb.facilities")}
    </BreadcrumbItem>
    <BreadcrumbSeparator />
    <BreadcrumbCurrent>
      <FacilityNameIcon style={{ width: 24, height: 24 }} />
      {current}
    </BreadcrumbCurrent>
  </FacilityBreadcrumbLeft>
);

const DetailField = ({ fieldKey, label, value }) => (
  <Col span={6}>
    <Field data-testid={`facility-field-${fieldKey}`}>
      <FieldLabel>{label}</FieldLabel>
      <FieldValue data-testid="facility-field-value">{dash(value)}</FieldValue>
    </Field>
  </Col>
);

export const FacilityDetail = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { track } = useStandardizedTracking();
  const facilities = useSelector(selectProvidersAllFacilities);
  const facilitySelector = useMemo(() => selectFacilityById(id), [id]);
  const facility = useSelector(facilitySelector);
  const users = useSelector(selectUsers) || {};
  const canEdit = useSelector(selectCanEditFacilities);
  const trackedFacilityId = useRef(null);

  const isLoaded = facilities.length > 0;

  useEffect(() => {
    if (!isLoaded) dispatch(ACTIONS.providersAction("facility"));
  }, [dispatch, isLoaded]);

  useEffect(() => {
    if (isLoaded && !facility) history.push("/facilities-settings");
  }, [isLoaded, facility, history]);

  // Fire the view event once per facility (not on every re-render / users refresh).
  useEffect(() => {
    if (!facility || trackedFacilityId.current === facility.id) return;
    trackedFacilityId.current = facility.id;
    track(FACILITY_EVENTS.DETAIL_VIEWED, {
      featureGroup: FACILITY_FEATURE_GROUP,
      pageKey: FACILITY_PAGE_KEY,
      contentKey: "facility_detail",
      additionalInfo: { facility_id: facility.id }
    });
  }, [facility, track]);

  if (!facility) {
    return (
      <CompactDetailPageContainer>
        <Spin />
      </CompactDetailPageContainer>
    );
  }

  const handleEdit = () => {
    track(FACILITY_EVENTS.EDIT_STARTED, {
      featureGroup: FACILITY_FEATURE_GROUP,
      pageKey: FACILITY_PAGE_KEY,
      contentKey: "edit_button",
      additionalInfo: { facility_id: facility.id, entry_point: "detail" }
    });
    history.push(`/facilities-settings/${facility.id}/edit`);
  };

  return (
    <CompactDetailPageContainer id="Facility_Detail_Page">
      <CompactDetailHeaderSection>
        <BreadcrumbSection>
          <FacilityBreadcrumb current={facility.name} history={history} t={t} />
          {canEdit && (
            <FacilityHeaderButton id="Facility_Edit" type="primary" onClick={handleEdit}>
              {t("facility_management.detail.button_edit")}
            </FacilityHeaderButton>
          )}
        </BreadcrumbSection>
        <PageHeading>{t("facility_management.detail.page_title")}</PageHeading>
        <FacilityMetaLine facility={facility} users={users} t={t} />
      </CompactDetailHeaderSection>

      <Section>
        <SectionTitle>{t("facility_management.detail.section_main")}</SectionTitle>
        <Row gutter={[24, 0]}>
          <DetailField fieldKey="name" label={t("facility_management.detail.label_name")} value={facility.name} />
          <DetailField
            fieldKey="matchingString"
            label={t("facility_management.detail.label_matching_string")}
            value={facility.providerMatchingAliases}
          />
          <DetailField fieldKey="npi" label={t("facility_management.detail.label_npi")} value={facility.npi} />
          <DetailField fieldKey="taxId" label={t("facility_management.detail.label_tax_id")} value={facility.taxId} />
        </Row>
      </Section>

      <Section>
        <SectionTitle>{t("facility_management.detail.section_location")}</SectionTitle>
        <Row gutter={[24, 0]}>
          <DetailField
            fieldKey="address"
            label={t("facility_management.detail.label_address")}
            value={facility.address}
          />
          <DetailField
            fieldKey="suiteNumber"
            label={t("facility_management.detail.label_suite")}
            value={facility.suiteNumber}
          />
          <DetailField fieldKey="city" label={t("facility_management.detail.label_city")} value={facility.city} />
          <DetailField fieldKey="state" label={t("facility_management.detail.label_state")} value={facility.state} />
        </Row>
        <Row gutter={[24, 0]}>
          <DetailField fieldKey="zipCode" label={t("facility_management.detail.label_zip")} value={facility.zipCode} />
        </Row>
      </Section>

      <Section>
        <SectionTitle>{t("facility_management.detail.section_contact")}</SectionTitle>
        <Row gutter={[24, 0]}>
          <DetailField
            fieldKey="phone"
            label={t("facility_management.detail.label_phone")}
            value={facility.phone ? formatPhoneNumber(facility.phone) : null}
          />
        </Row>
      </Section>
    </CompactDetailPageContainer>
  );
};

export default FacilityDetail;
